import React from "react";
import { Summary } from "../types";
import { ChartBarIcon } from "@heroicons/react/24/outline";

interface SummaryCardProps {
  summary: Summary;
  onOpenStats?: () => void;
}

export const SummaryCard: React.FC<SummaryCardProps> = ({ summary }) => {
  const stats = [
    {
      label: "Total Records",
      value: summary.total,
      color: "blue",
      gradient: "from-blue-500 to-blue-600",
      bgGradient: "from-blue-50 to-blue-100",
      icon: "📊",
    },
    {
      label: "Mapped Records",
      value: summary.mapped,
      color: "green",
      gradient: "from-green-500 to-emerald-600",
      bgGradient: "from-green-50 to-emerald-100",
      icon: "✅",
    },
    {
      label: "Pending Records",
      value: summary.pending,
      color: "orange",
      gradient: "from-orange-500 to-amber-600",
      bgGradient: "from-orange-50 to-amber-100",
      icon: "⏳",
    },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-1 w-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
        <h3 className="text-xl font-bold text-gray-800">
          Summary Statistics
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${stat.bgGradient} rounded-xl p-5 text-center transform transition-all duration-300 hover:scale-105 hover:shadow-lg border border-white/50`}
          >
            <div className="text-3xl mb-2">{stat.icon}</div>
            <div className={`text-3xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-1`}>
              {stat.value}
            </div>
            <div className="text-sm font-medium text-gray-700 mt-1">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Compact inline summary for header
export const CompactSummary: React.FC<SummaryCardProps> = ({ summary, onOpenStats }) => {
  return (
    <div className="flex items-center gap-3 text-xs">
      {/* Shadow Stats Button */}
      {onOpenStats && (
        <button
          onClick={onOpenStats}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-md shadow-sm hover:shadow-md transition-all duration-200 font-medium text-xs"
          title="View Shadow Index Statistics"
        >
          <ChartBarIcon className="h-4 w-4" />
          <span>Stats</span>
        </button>
      )}
      <div className="flex items-baseline gap-1.5 px-2.5 py-1.5 bg-blue-50/80 hover:bg-blue-50 rounded-md border border-blue-200/60 transition-colors">
        <span className="text-blue-700 font-bold text-sm">{summary.total}</span>
        <span className="text-blue-600/80 font-medium">Total</span>
      </div>
      <div className="flex items-baseline gap-1.5 px-2.5 py-1.5 bg-green-50/80 hover:bg-green-50 rounded-md border border-green-200/60 transition-colors">
        <span className="text-green-700 font-bold text-sm">{summary.mapped}</span>
        <span className="text-green-600/80 font-medium">Mapped</span>
      </div>
      <div className="flex items-baseline gap-1.5 px-2.5 py-1.5 bg-orange-50/80 hover:bg-orange-50 rounded-md border border-orange-200/60 transition-colors">
        <span className="text-orange-700 font-bold text-sm">{summary.pending}</span>
        <span className="text-orange-600/80 font-medium">Pending</span>
      </div>
    </div>
  );
};
